// components/crm/CrmEventTypeManagement.tsx
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Card,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { CrmEventType } from '../../api/CRMApiClient';

const CrmEventTypeManagement = observer(() => {
  const { t } = useTranslation();
  const { crmStore } = useStore();

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<CrmEventType | null>(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');

  useEffect(() => {
    crmStore.getCrmEventTypes();
  }, [crmStore]);

  const data = !search
    ? crmStore.CrmEventTypes
    : crmStore.CrmEventTypes.filter((x) => x.name.toLowerCase().includes(search.toLowerCase()));

  /* CRUD helpers (unchanged) */
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModal(true);
  };
  const openEdit = (t: CrmEventType) => {
    setEditing(t);
    form.setFieldsValue(t);
    setModal(true);
  };
  const save = async () => {
    const v = await form.validateFields();
    editing ? await crmStore.updateCrmEventType(editing.id, v) : await crmStore.addCrmEventType(v);
    setModal(false);
  };

  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: '10%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '70%' },
    {
      title: t('Actions'),
      key: 'actions',
      width: '20%',
      render: (_: unknown, r: CrmEventType) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title={t('DeleteCrmEventTypeConfirm')} onConfirm={() => crmStore.deleteCrmEventType(r.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={crmStore.isLoading}>
      <Card
        extra={
          <Space>
            <Input.Search
              allowClear
              placeholder={t('Search')}
              onSearch={setSearch}
              style={{ width: 200 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => setSearch('')} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              {t('AddCrmEventType')}
            </Button>
          </Space>
        }
      >
        <Table dataSource={data} columns={columns} rowKey="id" pagination={false} />

        <Modal
          title={editing ? t('EditCrmEventType') : t('AddCrmEventType')}
          open={modal}
          onOk={save}
          onCancel={() => setModal(false)}
          destroyOnClose
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Form form={form} layout="vertical">
            <Form.Item name="name" label={t('Name')} rules={[{ required: true, message: t('PleaseEnterName') }]}>
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </Spin>
  );
});

export default CrmEventTypeManagement;
